export interface SolutionData {
  slug: string;
  badge: string;
  title: string;
  description: string;
  color: string;
  icon: string;
  features: string[];
  benefits: string[];
  process: {
    title: string;
    description: string;
  }[];
}

export const solutions: SolutionData[] = [
  {
    slug: 'ai-connect',
    badge: 'AI Connect',
    title: 'AI Connect — Bring Your Own AI',
    description: 'Connect Claude, ChatGPT, and other AI clients directly to your portal via the Model Context Protocol. Your AI reads your CRM, drafts content, and builds pages — with scoped permissions and a human-in-the-loop approval step you control.',
    color: '#0891b2',
    icon: 'cable',
    features: [
      'Model Context Protocol (MCP) server built in',
      'Connect Claude Desktop, Claude Code & Claude.ai',
      'Connect Claude Desktop, Claude Code & Claude.ai today — plus ChatGPT where MCP connectors are available',
      'Scoped, permissioned API keys per client',
      'Bring-your-own AI provider keys (Anthropic & OpenAI)',
      'Approval workflow — AI proposes, you confirm',
    ],
    benefits: [
      'Use the AI tools you already love with your business data',
      'No copy-paste between AI and your portal',
      'Granular control over what AI can see and do',
      'Future-proof — built on an open standard (MCP)',
    ],
    process: [
      { title: 'Generate an API Key', description: 'Create a scoped key from your portal with read, write, or approve permissions.' },
      { title: 'Connect Your AI Client', description: 'Add the portal as an MCP server in Claude, ChatGPT, or your tool of choice.' },
      { title: 'Grant Scoped Access', description: 'Decide exactly which tools and data your AI assistant can reach.' },
      { title: 'Let AI Do the Work', description: 'Your AI drafts content, updates the CRM, and builds pages — each change goes through your approval queue.' },
    ],
  },
  {
    slug: 'websites',
    badge: 'Website Builder',
    title: 'Drag-and-Drop Website Builder',
    description: 'Build and publish professional websites without writing a line of code. A 47-block visual editor, built-in blog, SEO tools, and revision history — all managed from one dashboard.',
    color: '#3b82f6',
    icon: 'language',
    features: [
      'Visual drag-and-drop page editor (47 block types)',
      'Built-in blog with categories & tags',
      'SEO tools & meta management',
      'Revision history with one-click restore',
      'Custom branding & domain support',
      'One-click site export, import & cloning (snapshots)',
    ],
    benefits: [
      'Launch a professional site in hours, not weeks',
      'No developer needed for content updates',
      'Built-in SEO so customers can find you',
      'Full revision history — nothing is ever lost',
    ],
    process: [
      { title: 'Start Building', description: 'Start from a blank canvas or import a snapshot of an existing site — then customize with our block editor.' },
      { title: 'Customize Everything', description: 'Drag, drop, and style every element to match your brand. Add pages, blog posts, and products.' },
      { title: 'Connect Your Domain', description: 'Point your custom domain and go live with SSL, CDN, and managed hosting included.' },
      { title: 'Grow & Optimize', description: 'Use built-in SEO tools and revision history to keep your site sharp and evolving.' },
    ],
  },
  {
    slug: 'ecommerce',
    badge: 'Online Store',
    title: 'Online Store & Ecommerce',
    description: 'Build a product catalog with variants, discounts, shipping rules, and Stripe payments — all inside the same platform as your website and CRM. Designable products support per-item customization.',
    color: '#16a34a',
    icon: 'storefront',
    features: [
      'Product catalog with variants & SKUs',
      'Discount codes & bulk pricing rules',
      'Shipping zones & rate configuration',
      'Stripe payment integration',
      'Per-product customization options for designable products',
      'Customer accounts, order history & wishlists',
    ],
    benefits: [
      'Sell products without adding a separate Shopify subscription',
      'Offer custom product options where your catalog supports it',
      'Manage products, pricing, and shipping in one place',
      'Connected to your CRM, email, and analytics',
    ],
    process: [
      { title: 'Add Your Products', description: 'Create products with images, variants, options, and SKUs — including customization options for designable items.' },
      { title: 'Set Pricing & Shipping', description: 'Configure prices, discount codes, tax, and shipping zones for where you sell.' },
      { title: 'Launch Your Storefront', description: 'Your store lives on your own site with Stripe-powered payments and customer accounts.' },
      { title: 'Manage Orders', description: 'Track orders from payment received through to delivery in your dashboard.' },
    ],
  },
  {
    slug: 'publishing',
    badge: 'Content Calendar',
    title: 'Publishing & Editorial Calendar',
    description: 'Run your editorial board and content calendar from one command center. A kanban board moves ideas through Draft → Review → Scheduled, campaigns group content by date range, and granular per-user permissions keep the right people in the right lanes.',
    color: '#0d9488',
    icon: 'rocket_launch',
    features: [
      'Editorial kanban: Idea → Draft → Review → Scheduled → Published',
      'Month & week content calendar views',
      'Campaigns that group content by date range',
      'Link any artifact — post, email, deck, survey, booking page',
      'Checklists, assignees, comments & due dates per card',
      'Granular per-user publishing permissions',
    ],
    benefits: [
      'See your full content pipeline in one place',
      'Never miss a deadline or double-book a campaign',
      'Keep writers, designers, and approvers on the same board',
      'Connected to your CMS, email campaigns, decks, and surveys',
    ],
    process: [
      { title: 'Capture Ideas', description: 'Drop every content idea onto the board — blog posts, email campaigns, decks, and more.' },
      { title: 'Plan the Calendar', description: 'Schedule cards, group them into campaigns, and see your whole month at a glance.' },
      { title: 'Move It Forward', description: 'Assign owners, add checklists, and advance cards through review to scheduled.' },
      { title: 'Publish & Track', description: 'Ship content and watch each piece land on the published lane.' },
    ],
  },
  {
    slug: 'email-marketing',
    badge: 'Email Marketing',
    title: 'Email Campaigns That Convert',
    description: 'Send beautiful email campaigns, manage subscriber lists, and track engagement — all from the same platform where you manage your website and CRM. Real-time co-editing and A/B subject testing included.',
    color: '#8b5cf6',
    icon: 'email',
    features: [
      'Visual email campaign builder with real-time co-editing',
      'Subscriber list management',
      'A/B subject-line testing with auto-promote winner',
      'Open & click tracking analytics',
      'Domain authentication & deliverability (Resend)',
      'Per-recipient unsubscribe links',
    ],
    benefits: [
      'Keep your audience engaged with targeted campaigns',
      'Test subject lines and promote the winner automatically',
      'Track exactly what resonates with your audience',
      'All your contacts in one place — no CSV exports',
    ],
    process: [
      { title: 'Import or Build Your List', description: 'Add subscribers manually or import a CSV — then grow your list as contacts come in.' },
      { title: 'Design Your Campaign', description: 'Use the visual editor to create branded emails that look great on every device — collaborate in real time.' },
      { title: 'A/B Test & Send', description: 'Set up a subject-line A/B test and send; the platform promotes the winner automatically after 4 hours.' },
      { title: 'Measure & Improve', description: 'Track opens and clicks to understand what resonates and plan your next send.' },
    ],
  },
  {
    slug: 'crm',
    badge: 'CRM',
    title: 'Customer Relationship Management',
    description: 'A full sales pipeline — contacts, companies, deals, proposals, and contracts — with custom fields, saved views, activity timelines, and notification fan-out. All your customer data in one connected place.',
    color: '#0ea5e9',
    icon: 'groups',
    features: [
      'Contact & company management with custom fields',
      'Deal pipeline with DnD kanban and custom stages',
      'Proposal builder with e-signature',
      'Activity timeline per contact',
      'Saved views, debounced search & multi-select filters',
      'Two-step CSV import/export',
    ],
    benefits: [
      'Never lose track of a lead or follow-up',
      'See your entire sales pipeline at a glance',
      'Build and send proposals without leaving the CRM',
      'Connected to your email, website, and booking tools',
    ],
    process: [
      { title: 'Add Your Contacts', description: 'Import existing contacts or add them as they come in from your website and booking pages.' },
      { title: 'Track Deals', description: 'Create deals, move them through pipeline stages via drag-and-drop, and attach notes and files.' },
      { title: 'Build Proposals', description: 'Assemble branded proposals with line items and e-signature directly from the deal record.' },
      { title: 'Close & Grow', description: 'Win deals, track revenue, and use saved views to focus on what needs attention.' },
    ],
  },
  {
    slug: 'contracts',
    badge: 'Contracts & E-Sign',
    title: 'Proposals & E-Signature',
    description: 'Build branded proposals with live pricing totals and collect legally binding e-signatures — all without leaving the platform. Every signed document ties back to its CRM deal with a full audit trail.',
    color: '#b45309',
    icon: 'draw',
    features: [
      'Branded proposal & contract builder',
      'Legally binding e-signatures (DropboxSign)',
      'Line items, fees & pricing tables with live totals',
      'Signature status tracking per document',
      'Timestamped audit trail for every signing event',
      'Public signing links for clients',
    ],
    benefits: [
      'Close deals faster with polished, signable proposals',
      'Replace DocuSign and PandaDoc with one tool',
      'Full audit trail shows when a client views and signs',
      'Every contract tied to its CRM deal',
    ],
    process: [
      { title: 'Build the Proposal', description: 'Assemble sections, line items, terms, and pricing into a branded proposal or contract.' },
      { title: 'Share a Signing Link', description: 'Generate a secure public link — your client reviews the proposal and signs from any device.' },
      { title: 'Client Signs Online', description: 'Legally binding e-signature captures intent, timestamp, and a full audit trail.' },
      { title: 'Deal Closed & Tracked', description: 'The signed document attaches to the deal and your pipeline updates automatically.' },
    ],
  },
  {
    slug: 'invoicing',
    badge: 'Invoicing',
    title: 'Invoicing & Payments',
    description: 'Bill clients and get paid without leaving the platform. Send itemized invoices, track what\'s due, and let clients pay securely by card — with everything tied back to your deals and services.',
    color: '#0369a1',
    icon: 'receipt_long',
    features: [
      'Itemized invoices with line items & quantities',
      'Subtotal, tax & grand-total breakdown',
      'Secure card payment powered by Stripe',
      'Status tracking: draft, sent & paid',
      'In-portal services catalog clients can purchase',
      'Per-client scoping with online self-serve payment',
    ],
    benefits: [
      'Get paid faster with one-click online payment',
      'No separate invoicing tool to reconcile',
      'Clients see exactly what they\'re paying for',
      'Sell add-on services right from the portal',
    ],
    process: [
      { title: 'Build the Invoice', description: 'Add line items, quantities, and pricing for any service or project.' },
      { title: 'Send It Over', description: 'Share a secure link; your client sees an itemized breakdown with subtotal and tax.' },
      { title: 'Client Pays Online', description: 'They pay securely by card through Stripe — no account or back-and-forth required.' },
      { title: 'Tracked & Reconciled', description: 'Status flips to paid, the timestamp is recorded, and revenue lands in your dashboard.' },
    ],
  },
  {
    slug: 'booking',
    badge: 'Scheduling',
    title: 'Online Booking & Scheduling',
    description: 'Full white-label scheduling for your business — group classes, individual appointments, and Stripe payments, all in a single embeddable booking page. The platform supports waivers and gift certificates.',
    color: '#10b981',
    icon: 'calendar_month',
    features: [
      'Embeddable booking pages (individual & group)',
      'Waiver and gift-certificate support',
      'Stripe payment collection at booking',
      'Automated email reminders',
      'Custom availability, staff rules & Zoom integration',
      'Timezone-aware scheduling',
    ],
    benefits: [
      'Eliminate the back-and-forth of scheduling',
      'Collect payment and waivers at booking',
      'Reduce no-shows with automatic reminders',
      'Clients can self-serve 24/7 from your website',
    ],
    process: [
      { title: 'Set Your Availability', description: 'Define available hours, buffer times between appointments, and advance booking windows.' },
      { title: 'Create Booking Pages', description: 'Build branded booking pages for different services, staff members, or event types.' },
      { title: 'Share or Embed', description: 'Share a link or embed the booking widget directly on any page of your website.' },
      { title: 'Manage Appointments', description: 'View upcoming bookings, trigger reminders, and check customers in with a code.' },
    ],
  },
  {
    slug: 'surveys',
    badge: 'Surveys & Forms',
    title: 'Surveys, Forms & Lead Capture',
    description: 'Build smart forms, quizzes, and surveys that do more than collect answers — 15+ field types, branching logic, scoring, A/B variants, and response routing into your CRM.',
    color: '#e11d48',
    icon: 'ballot',
    features: [
      'Visual form builder (15+ field types)',
      'Conditional branching & page-jump logic',
      'Per-answer scoring & NPS',
      'A/B variant testing for surveys',
      'Response routing to your CRM',
      'Embeddable anywhere + HMAC webhooks',
    ],
    benefits: [
      'Qualify leads automatically as they respond',
      'Replace Typeform and Jotform with one tool',
      'Turn responses into CRM contacts and deals',
      'Recommend the right product or next step instantly',
    ],
    process: [
      { title: 'Build Your Form', description: 'Add questions and fields with a visual editor — multi-step, multiple choice, ratings, and more.' },
      { title: 'Add Logic & Scoring', description: 'Branch based on answers, score responses, and configure recommendations.' },
      { title: 'Share or Embed', description: 'Publish a public link or embed the form on any page of your site.' },
      { title: 'Capture & Route Leads', description: 'Responses flow into your CRM, trigger follow-ups, and fire webhooks automatically.' },
    ],
  },
  {
    slug: 'experiments',
    badge: 'A/B Testing',
    title: 'A/B Experiments & Optimization',
    description: 'Test what actually converts on your website pages. Split traffic deterministically, track goal completions, and let built-in statistical significance tell you when a result is real — no third-party testing tool required.',
    color: '#65a30d',
    icon: 'science',
    features: [
      'Split-test website pages',
      'Automatic, stable visitor bucketing',
      'Custom traffic-split weights per variant',
      'Built-in significance testing (lift & p-value)',
      'Goal metric & conversion tracking per test',
      'Subject-line A/B testing for email campaigns',
    ],
    benefits: [
      'Make decisions with data, not guesses',
      'Lift conversions on the pages that matter most',
      'Know when a result is real, not random',
      'No third-party testing tool required',
    ],
    process: [
      { title: 'Pick What to Test', description: 'Choose a website page or an email subject line to experiment on.' },
      { title: 'Create Variants', description: 'Set up two or more versions and assign the traffic split you want for each.' },
      { title: 'Split Traffic Automatically', description: 'Visitors are bucketed consistently, so each person always sees the same variant.' },
      { title: 'Declare the Winner', description: 'Watch lift and p-value update live, then ship the variant that proves it converts.' },
    ],
  },
  {
    slug: 'project-management',
    badge: 'Projects',
    title: 'Project Management & Collaboration',
    description: 'Kanban boards, sprint planning, and burndown analytics in one portal. WIP limits, My-Tasks inbox, and velocity reports keep your team moving without a separate project tool.',
    color: '#4f46e5',
    icon: 'view_kanban',
    features: [
      'Kanban boards with drag-and-drop',
      'Sprint planning, burndown & velocity reports',
      'WIP limits with server-side enforcement',
      'My-Tasks unified inbox across all projects',
      'Team assignments, watchers & comments',
      'Checklists, dependencies & attachments',
    ],
    benefits: [
      'See every project at a glance',
      'Keep your team aligned and accountable',
      'Enforce WIP limits so work actually finishes',
      'Works seamlessly with your other portal tools',
    ],
    process: [
      { title: 'Create a Project', description: 'Set up a project with goals, team members, and a Kanban board.' },
      { title: 'Break It Down', description: 'Add tasks, assign owners, and organize work into sprints or phases.' },
      { title: 'Track Progress', description: 'Move cards across your board, enforce WIP limits, and check the burndown daily.' },
      { title: 'Ship & Review', description: 'Mark milestones complete and use velocity reports to plan the next sprint.' },
    ],
  },
  {
    slug: 'help-desk',
    badge: 'Help Desk',
    title: 'Shared Inbox & Support Tickets',
    description: 'A structured support queue with SLA deadlines, staff assignment, and internal notes — so every customer request stays on track and nothing slips through the cracks.',
    color: '#ea580c',
    icon: 'support_agent',
    features: [
      'Shared team inbox for all support conversations',
      'Conversation status: open, assigned & closed',
      'Support tickets with priority, category & SLA',
      'SLA countdown badges for first response & resolution',
      'Internal notes (hidden from clients)',
      'Assign to teammates & flag overdue tickets',
    ],
    benefits: [
      'Answer every customer from one shared queue',
      'Never let a conversation or ticket slip through the cracks',
      'Hit your response targets with visible SLA tracking',
      'Keep internal discussion separate from client replies',
    ],
    process: [
      { title: 'Open a Ticket', description: 'Create structured tickets for incoming requests and set priority, category, and SLA.' },
      { title: 'Assign & Respond', description: 'Claim a ticket, add internal notes for the team, and reply to the client — all in one place.' },
      { title: 'Track Deadlines', description: 'SLA countdown badges stay visible until first response and resolution deadlines are met.' },
      { title: 'Close & Follow Up', description: 'Mark tickets resolved and keep the conversation thread for future reference.' },
    ],
  },
  {
    slug: 'company-brain',
    badge: 'Company Brain',
    title: 'Company Brain — Your AI Knowledge Base',
    description: 'Capture institutional knowledge in notes, decisions, and playbooks — then search it all with hybrid AI (keyword + semantic). Paste text to let the AI propose structured knowledge entries; a human-approval step keeps everything trustworthy.',
    color: '#7c3aed',
    icon: 'psychology',
    features: [
      'Hybrid AI search — keyword + semantic (pgvector)',
      'Knowledge base with notes, wikilinks & link graph',
      'AI-proposed entries with human-approval queue',
      'Decisions log & operating playbooks',
      'Org chart, people & expertise finder',
      'Initiatives, goals & business glossary',
    ],
    benefits: [
      'Stop losing knowledge when people leave',
      'Search your own business with cited, sourced answers',
      'Review AI-proposed knowledge before it goes live',
      'Onboard new hires in days, not months',
    ],
    process: [
      { title: 'Add Your Knowledge', description: 'Create notes, log decisions, and write playbooks — or paste text and let the AI propose entries.' },
      { title: 'Review & Approve', description: 'The AI suggests structured knowledge; your team reviews and approves before it\'s indexed.' },
      { title: 'Search Anything', description: 'Type a question and get hybrid-search results linked back to the source records.' },
      { title: 'Keep It Growing', description: 'Add new notes, decisions, and playbooks as your team learns — and explore Google Workspace integration (white-glove setup available).' },
    ],
  },
  {
    slug: 'ai-chatbot',
    badge: 'Live Chat',
    title: 'Live Chat Widget & Visitor Inbox',
    description: 'Deploy a branded live-chat widget on your website and handle every conversation from a shared team inbox. Visitors start a chat; your team picks up, replies, and closes — all in one place.',
    color: '#a855f7',
    icon: 'chat',
    features: [
      'Embeddable chat widget for any website',
      'Real-time visitor↔agent messaging',
      'Customizable greeting, color & away message',
      'Secure HMAC-authenticated sessions',
      'Live human handoff via shared team inbox',
      'Conversation history & status tracking',
    ],
    benefits: [
      'Engage visitors before they bounce',
      'Handle every website chat from one team inbox',
      'Seamless handoff from widget to support ticket',
      'Branded experience — your colors, your tone',
    ],
    process: [
      { title: 'Configure the Widget', description: 'Set the greeting, color, and away message to match your brand.' },
      { title: 'Embed on Your Site', description: 'Add the chat widget to any page with a single embed snippet.' },
      { title: 'Conversations Land', description: 'Visitor messages arrive in your real-time shared inbox — no polling required.' },
      { title: 'Reply & Close', description: 'Claim a conversation, respond in real time, and mark it closed when done.' },
    ],
  },
  {
    slug: 'automations',
    badge: 'Automations',
    title: 'Automations & Workflows',
    description: 'A no-code rule engine that fires on real platform events — bookings, CRM moves, email subscribers, and more. Describe a rule in plain language and the AI parses it; or build one yourself with triggers, conditions, and actions.',
    color: '#db2777',
    icon: 'account_tree',
    features: [
      'Rule engine wired to bookings, CRM, email lists & support tickets',
      'NLP rule creation — describe a rule in plain language',
      'Triggers: booking confirmed/cancelled, deal moved, subscriber added & more',
      'Actions: send email, run Brain playbook',
      'Conditions: branch on field values, tags, or deal stage',
      'Scheduled & event-based automation',
    ],
    benefits: [
      'Automate follow-ups without Zapier',
      'Never drop a lead or forget a next step',
      'Connect tools that already share your data',
      'Set it once and let it run',
    ],
    process: [
      { title: 'Pick a Trigger', description: 'Choose what starts the rule — a booking confirmed, a subscriber added, a deal stage change, or a schedule.' },
      { title: 'Add Conditions', description: 'Branch logic based on field values, tags, or deal stage.' },
      { title: 'Choose an Action', description: 'Send an email or run a Brain playbook when the rule fires.' },
      { title: 'It Runs Automatically', description: 'The rule fires every time the trigger happens — no manual work required.' },
    ],
  },
  {
    slug: 'pitch-decks',
    badge: 'Pitch Decks',
    title: 'AI-Powered Pitch Decks',
    description: 'Generate complete, brand-aware pitch decks from a prompt. The AI pulls your brand profile, extracts context from your website, and produces structured slides — then you edit, version, and publish per-slide.',
    color: '#f59e0b',
    icon: 'slideshow',
    features: [
      'AI-generated slides from a prompt or business brief',
      'Brand context pipeline (profile → client → URL-extract)',
      'Per-slide AI editing with smart payload optimization',
      'Auto-version snapshot before every AI generation',
      'Draft/publish per-slide staging',
      'Shareable presentation links',
    ],
    benefits: [
      'Go from brief to complete deck in minutes',
      'Brand-consistent slides without a designer',
      'Safe to iterate — auto-versioned before every AI edit',
      'Share a link instead of emailing attachments',
    ],
    process: [
      { title: 'Enter Your Brief', description: 'Describe your business, product, and goals — the AI pulls brand context from your profile and website.' },
      { title: 'AI Generates Slides', description: 'A complete branded deck is created with the right structure, copy, and layout.' },
      { title: 'Edit Slide by Slide', description: 'Refine individual slides with AI or manually — every generation auto-snapshots so nothing is lost.' },
      { title: 'Publish & Share', description: 'Publish slides individually and send a shareable link to your audience.' },
    ],
  },
  {
    slug: 'agency',
    badge: 'White-Label',
    title: 'Agency & White-Label Platform',
    description: 'Run the entire platform under your own brand. Map a custom domain with DNS verification, swap in your logo and agency name, and serve clients from your portal — not ours.',
    color: '#c026d3',
    icon: 'storefront',
    features: [
      'Custom domain with DNS ownership verification',
      'Your agency name across the whole portal',
      'Your logo in place of the platform icon',
      'White-label login & portal chrome',
      'Manage multiple client companies in one login',
      'Domain audit trail & tag-based cache invalidation',
    ],
    benefits: [
      'Resell the platform as your own product',
      'Present a seamless, branded experience to clients',
      'Win bigger retainers with an all-in-one offering',
      'No “powered by” — it\'s your brand, end to end',
    ],
    process: [
      { title: 'Map Your Domain', description: 'Point a custom domain at the portal and verify ownership with a simple DNS TXT record.' },
      { title: 'Add Your Brand', description: 'Upload your logo and set your agency name — the portal chrome updates immediately.' },
      { title: 'Flip to White-Label', description: 'Enable white-label mode and the portal, login, and navigation become entirely yours.' },
      { title: 'Serve Your Clients', description: 'Onboard client companies and switch between them from a single branded login.' },
    ],
  },
  {
    slug: 'hosting',
    badge: 'Hosting',
    title: 'Managed Hosting & Infrastructure',
    description: 'See your live environment status, DNS, and URLs directly in the portal — no SSH required. Admins provision and manage infrastructure; clients get a clear read-only view of what is live and what is pending.',
    color: '#64748b',
    icon: 'cloud',
    features: [
      'Live environment status dashboard',
      'DNS management with real CNAME instructions',
      'Guided DNS verification with admin-verified promotion',
      'Client detail: provisioning / suspended / active states',
      'Admin CRUD panel with search & slide-out',
      'Custom domain support',
    ],
    benefits: [
      'Clients see exactly what is live — no back-and-forth emails',
      'Admins review and verify environments before go-live',
      'Clear DNS instructions and status updates throughout',
      'Zero server management for your clients',
    ],
    process: [
      { title: 'Provision the Environment', description: 'Admin creates the hosting record and sets the DNS target in the management panel.' },
      { title: 'Update DNS', description: 'Client follows the provided CNAME instructions in their domain registrar.' },
      { title: 'Verify & Promote', description: 'Admin verifies DNS resolution and promotes the environment to active.' },
      { title: 'Monitor the Dashboard', description: 'Client sees live status, URLs, and environment health at a glance from their portal.' },
    ],
  },
];

// Slugs hidden from the public marketing site: excluded from the /solutions
// listing + sitemap and their detail pages 404. Reversible — remove a slug
// here to restore it (the data below is kept intact).
const HIDDEN_SLUGS = new Set<string>(['invoicing']);

export function getSolutionBySlug(slug: string): SolutionData | undefined {
  if (HIDDEN_SLUGS.has(slug)) return undefined;
  return solutions.find((solution) => solution.slug === slug);
}

export function getAllSolutions(): SolutionData[] {
  return solutions.filter((solution) => !HIDDEN_SLUGS.has(solution.slug));
}
